import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import {
  CLIENT_DOCUMENT_REQUESTS_QUERY_KEY,
  CLIENT_MODULE_ENTRIES_QUERY_KEY,
  createDocumentRequest,
  fetchModuleEntries,
} from "./clientDocuments.api";
import { assigneeOptionsFor, buildRequestPayload, moduleEntryLabel } from "./requestRouting";

// Module-scoped counter so each item row gets a stable React key across
// add/remove without pulling in a uuid dependency for a purely local id.
let itemIdCounter = 0;
const emptyItem = () => ({
  id: `item-${itemIdCounter++}`,
  label: "",
  instructions: "",
  required: true,
  maxFiles: "",
});

/**
 * Client-side mirror of the backend's maxFiles rule: null means unlimited,
 * otherwise it must be a whole number of at least 1. The backend rejects a
 * non-positive value by naming the offending item, so we validate here too
 * to give the same feedback before a round trip.
 */
const validateForm = (title, items) => {
  if (!title.trim()) {
    return "Title is required.";
  }
  if (items.length === 0) {
    return "Add at least one item.";
  }
  for (const item of items) {
    if (!item.label.trim()) {
      return "Every item needs a label.";
    }
    if (item.maxFiles !== "") {
      const parsed = Number(item.maxFiles);
      if (!Number.isInteger(parsed) || parsed < 1) {
        return `Max files for "${item.label.trim()}" must be a whole number of at least 1.`;
      }
    }
  }
  return null;
};

const namedItemCount = (items) => items.filter((item) => item.label.trim()).length;

// Entry ids are only unique within a module, so a target is keyed by both.
const targetKey = (target) => (target ? `${target.moduleType}-${target.entryId}` : "");

/**
 * `defaultTarget` ({ moduleType, entryId, label }) pre-selects where the documents are filed, for
 * when the dialog is opened from inside a module (Visa, RPL, Admission…) rather than the client.
 */
const NewDocumentRequestDialog = ({ clientId, open, onClose, defaultTarget = null }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [formError, setFormError] = useState(null);
  const [assignee, setAssignee] = useState("CLIENT");
  const [targetEntryId, setTargetEntryId] = useState(() => targetKey(defaultTarget));

  // Fresh on every open: the client's partner attribution may have changed since last time.
  const { data: client } = useQuery({
    queryKey: ["client-for-document-request", clientId],
    queryFn: () => axios.get(`${API_URL}/clients/${clientId}`).then((r) => r.data?.data ?? null),
    enabled: open && Boolean(clientId),
    refetchOnMount: true,
  });

  const { data: moduleEntries = [] } = useQuery({
    queryKey: [CLIENT_MODULE_ENTRIES_QUERY_KEY, clientId],
    queryFn: () => fetchModuleEntries(clientId),
    enabled: open && Boolean(clientId),
    refetchOnMount: true,
  });

  const assigneeOptions = assigneeOptionsFor(client || {});
  // The module's own entry may not be in the client's entry list yet (just created, or filtered
  // out), so it is offered regardless.
  const targetOptions =
    defaultTarget && !(moduleEntries || []).some((e) => targetKey(e) === targetKey(defaultTarget))
      ? [defaultTarget, ...(moduleEntries || [])]
      : moduleEntries || [];
  const selectedTarget = targetOptions.find((entry) => targetKey(entry) === targetEntryId);

  useEffect(() => {
    if (open) {
      setAssignee("CLIENT");
      setTargetEntryId(targetKey(defaultTarget));
    }
    // defaultTarget is read on open only, so an edit in progress isn't reset by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setTitle("");
    setItems([emptyItem()]);
    setFormError(null);
    setAssignee("CLIENT");
    setTargetEntryId(targetKey(defaultTarget));
  };

  const handleClose = () => {
    if (mutation.isLoading) return;
    resetForm();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (body) => createDocumentRequest(clientId, body),
    onSuccess: () => {
      toast.success("Document request sent.");
      queryClient.invalidateQueries([CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, clientId]);
      resetForm();
      onClose();
    },
    onError: (error) => {
      toast.error(error.message);
      // The request is created before the invite is mailed, so a send failure (a client with no
      // email on file) still leaves a real request behind. Refresh so it is visible and can be
      // resent, rather than appearing only after an unrelated reload.
      queryClient.invalidateQueries([CLIENT_DOCUMENT_REQUESTS_QUERY_KEY, clientId]);
    },
  });

  const updateItem = (index, changes) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...changes } : item)));
  };

  const addItem = () => setItems([...items, emptyItem()]);

  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Creating a request emails the client immediately, so validate first, then confirm,
  // and only send once the user has agreed to the email going out.
  const handleSubmit = () => {
    const error = validateForm(title, items);
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setConfirmOpen(true);
  };

  const handleConfirmedSend = () => {
    setConfirmOpen(false);
    mutation.mutate(
      buildRequestPayload({
        title,
        items,
        assignee,
        assigneePartnerId: client?.referralPartnerId,
        targetModule: selectedTarget
          ? { moduleType: selectedTarget.moduleType, entryId: selectedTarget.entryId }
          : null,
      })
    );
  };

  const confirmMessage =
    assignee === "PARTNER"
      ? `The referral partner collects "${title.trim()}" and confirms it in — the client gets no email unless the partner forwards the link.`
      : `This emails the client an upload link for "${title.trim()}" straight away, listing ${namedItemCount(
          items
        )} document${
          namedItemCount(items) === 1 ? "" : "s"
        }. They can start uploading as soon as they receive it.`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Request documents
        {defaultTarget ? (
          <Typography component="span" variant="body2" className="!ml-2 text-app-text-secondary">
            · {moduleEntryLabel(defaultTarget)}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent className="space-y-4">
        <TextField
          label="Request title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="dense"
          autoFocus
        />

        <Typography variant="subtitle2" className="text-app-text-secondary pt-1">
          Documents to collect ({items.length})
        </Typography>
        <Box className="space-y-3">
          {items.map((item, index) => (
            <Box key={item.id} className="border border-app-border rounded-lg p-3 space-y-2">
              <Box className="flex items-center justify-between gap-2">
                <Typography variant="body2" className="font-semibold">
                  Document {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeItem(index)}
                  disabled={items.length === 1}
                  aria-label={`Remove document ${index + 1}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                label="Item label"
                value={item.label}
                onChange={(e) => updateItem(index, { label: e.target.value })}
                fullWidth
                size="small"
                placeholder="e.g. Passport bio page"
              />
              <TextField
                label="Instructions (optional)"
                value={item.instructions}
                onChange={(e) => updateItem(index, { instructions: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <Box className="flex items-center gap-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.required}
                      onChange={(e) => updateItem(index, { required: e.target.checked })}
                    />
                  }
                  label="Required"
                />
                <TextField
                  label="Max files (optional)"
                  type="number"
                  value={item.maxFiles}
                  onChange={(e) => updateItem(index, { maxFiles: e.target.value })}
                  size="small"
                  inputProps={{ min: 1, step: 1 }}
                  className="w-40"
                />
              </Box>
            </Box>
          ))}
        </Box>

        <Button startIcon={<AddCircleOutlineIcon />} onClick={addItem} size="small">
          Add document
        </Button>

        <Typography variant="subtitle2" className="text-app-text-secondary pt-1">
          Delivery
        </Typography>
        <FormControl component="fieldset">
          <FormLabel component="legend">Send to</FormLabel>
          <RadioGroup row value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            {assigneeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <TextField
          select
          fullWidth
          size="small"
          label="File the documents into (optional)"
          value={targetEntryId}
          onChange={(e) => setTargetEntryId(e.target.value)}
          helperText="Collected documents will appear in this entry's Documents tab as soon as they are submitted."
        >
          <MenuItem value="">No target — collect only</MenuItem>
          {targetOptions.map((entry) => (
            <MenuItem key={targetKey(entry)} value={targetKey(entry)}>
              {moduleEntryLabel(entry)}
            </MenuItem>
          ))}
        </TextField>

        {formError && (
          <Typography color="error" variant="body2">
            {formError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={mutation.isLoading}>
          {mutation.isLoading ? "Sending..." : "Send request"}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmOpen}
        title="Send this document request?"
        message={confirmMessage}
        confirmLabel="Send request"
        loading={mutation.isLoading}
        onConfirm={handleConfirmedSend}
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default NewDocumentRequestDialog;
